/**
 * Domain: category -> MIDI routing.
 *
 * Keeps knowledge of category IDs/names and default routing assignment.
 * No I/O here (no console, no fetch).
 */

/**
 * Category Name Mapping (copied from export-all-categories.js)
 * Keys are strings so they survive JSON/UI usage.
 */
export const CATEGORY_NAMES: Record<string, string> = {
  "-1": "N/A", "0": "Ground", "1": "Clear", "2": "Rain?", "3": "Snow?",
  "4": "Cloud?", "5": "Heavy rain", "6": "Heavy mixed", "7": "No rain/ice",
  "8": "Liquid cloud", "9": "Drizzling cloud", "10": "Warm rain", "11": "Cold rain",
  "12": "Melting snow", "13": "Snow (possible liquid)", "14": "Snow (no liquid)",
  "15": "Rimed snow (possible liquid)", "16": "Rimed snow and supercooled liquid",
  "17": "Snow and supercooled liquid", "18": "Supercooled liquid",
  "19": "Ice cloud (possible liquid)", "20": "Ice and supercooled liquid",
  "21": "Ice cloud (no liquid)", "22": "Stratospheric ice", "23": "STS (PSC Type I)",
  "24": "NAT (PSC Type II)", "25": "Insects", "26": "Dust", "27": "Sea salt",
  "28": "Continental pollution", "29": "Smoke", "30": "Dusty smoke",
  "31": "Dusty mix", "32": "Stratospheric ash", "33": "Stratospheric sulfate",
  "34": "Stratospheric smoke"
};

/**
 * Get sorted numeric category IDs.
 */
export function getKnownCategoryIds(): number[] {
  return Object.keys(CATEGORY_NAMES)
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
}

/**
 * Route configuration for a single category.
 */
export interface CategoryRoute {
  enabled: boolean;
  port: number;
  channel: number;
}

/**
 * Mapping of category ID (as string) to its route configuration.
 */
export type CategoryRouting = Record<string, CategoryRoute>;

/**
 * Assign a deterministic default routing for all known categories.
 * Uses 1-based MIDI channels (1..16) and 1-based port indices.
 */
export function createDefaultCategoryRouting(): CategoryRouting {
  const ids = getKnownCategoryIds();
  const routing: CategoryRouting = {};

  // map ids to an index 0..N-1 so we can spread them across ports/channels.
  ids.forEach((id, idx) => {
    const port = 1 + Math.floor(idx / 16);
    const channel = 1 + (idx % 16);
    routing[String(id)] = { enabled: true, port, channel };
  });

  return routing;
}

/**
 * Reason why a category route was or wasn't resolved.
 */
export type RouteResolveReason = 'ok' | 'disabled' | 'unknown' | 'invalid';

/**
 * Result of resolving a category route.
 */
export interface RouteResolveResult {
  route: { port: number; channel: number } | null;
  reason: RouteResolveReason;
}

/**
 * Resolve routing for a category.
 *
 * @param categoryId - category ID (string or number)
 * @param routing - the routing configuration
 * @returns resolution result with route (if found) and reason
 */
export function resolveCategoryRoute(
  categoryId: string | number,
  routing: CategoryRouting
): RouteResolveResult {
  const key = String(categoryId);
  if (!routing || typeof routing !== 'object') {
    return { route: null, reason: 'invalid' };
  }
  const r = routing[key];
  if (!r) {
    return { route: null, reason: 'unknown' };
  }
  if (!r.enabled) {
    return { route: null, reason: 'disabled' };
  }
  const port = Number(r.port);
  const channel = Number(r.channel);
  if (!Number.isFinite(port) || !Number.isFinite(channel)) {
    return { route: null, reason: 'invalid' };
  }
  return { route: { port, channel }, reason: 'ok' };
}

